import * as t from 'io-ts';

import type { CheckBoxDefinition } from 'header/types';
import { QueryType } from 'portlet/models/query';

import type {
    QueryFilterDataItem} from '../../query/systems/bw';
import {
    formatRangeBW,
    getActiveQueryFilterDataItems,
    getConditionQueryItems
} from '../../query/systems/bw';
import type { SettingValueMap } from '../../settings';
import { createSpecSettingsInterface, define } from './_common';

export interface HighlightOnFilterInCurrentDrillSettingDefinition extends CheckBoxDefinition {
    key: 'HIGHLIGHT_ON_FILTER_IN_CURRENT_DRILL';
}

const definition: HighlightOnFilterInCurrentDrillSettingDefinition = {
    key: 'HIGHLIGHT_ON_FILTER_IN_CURRENT_DRILL',
    type: 'CheckBox',
    label: 'Highlight on filter in current drill',
    value: false,
    scope: 'designer',
};

const specSettingsInterface = createSpecSettingsInterface(definition, t.boolean);

export const codec = specSettingsInterface.input;

export const HighlightOnFilterInCurrentDrillSettingDefinition = define({
    key: 'HIGHLIGHT_ON_FILTER_IN_CURRENT_DRILL',
    label: 'highlightOnFilterInCurrentDrill',
    definition,
    fromSetting: settings =>
        specSettingsInterface.extractValue(settings.highlightOnFilterInCurrentDrill),
    createDefinitionWithOpts: (opts = {}) => ({ ...definition, ...opts }),
    createDefinitionFromSpec: settings =>
        specSettingsInterface.createDefinitionWithSettings(
            settings.highlightOnFilterInCurrentDrill,
        ),
    toSignals: (
        definition,
        { mergedSettings, currentViewSignals, filters, queryMetadata, filterToSignalName },
    ) => {
        const signalName = 'HIGHLIGHT_ON_FILTER_IN_CURRENT_DRILL';
        const defaultReturn = [{ name: signalName, value: definition.value }];

        if (!queryMetadata) {
            return defaultReturn;
        }

        switch (queryMetadata.type) {
            case QueryType.BW: {
                const { conditionQueryFilter } = getConditionQueryItems(
                    mergedSettings,
                    queryMetadata,
                );

                const queryFilterDataItems = getActiveQueryFilterDataItems(
                    currentViewSignals,
                    filters,
                    queryMetadata,
                    filterToSignalName,
                    conditionQueryFilter,
                    formatRangeBW,
                );
                const omitedQueryFilters = omitFiltersInCurrentDrill(
                    queryFilterDataItems,
                    mergedSettings,
                    definition,
                ).map(filter => filter.value);

                return [
                    {
                        name: signalName,
                        value: omitedQueryFilters,
                    },
                ];
            }

            case QueryType.Databricks: {
                if (
                    !mergedSettings.DATABRICKS_DRILL ||
                    !mergedSettings.DATABRICKS_DRILL.length ||
                    !definition.value
                )
                    return [];

                const currentDrillKeys = mergedSettings.DATABRICKS_DRILL.map(({ key }) => key);
                const filterValues = filters.map(item => item.item);
                const fromQuery = queryMetadata.tables
                    .flatMap(item => item.columns)
                    .filter(
                        item =>
                            item.drillable &&
                            item.filter &&
                            filterValues.includes(item.filter.listenTo),
                    );

                const filterKeys = filters
                    .filter(
                        item =>
                            fromQuery.map(elem => elem.filter!.listenTo).includes(item.item) &&
                            fromQuery
                                .map(elem => elem.name)
                                .some(elem => currentDrillKeys.includes(elem)),
                    )
                    .map(item => item.value)
                    .filter(item => item.type === 'DIMENSION' || item.type === 'HIERARCHY')
                    .reduce<string[]>((pre, cur) => {
                        const dimension =
                            cur.type === 'DIMENSION' ? cur.value.flatMap(item => item.value) : [];
                        const hierarchy =
                            cur.type === 'HIERARCHY'
                                ? cur.value.values.flatMap(item => item.value)
                                : [];

                        return pre.concat(dimension, hierarchy);
                    }, []);

                return [{ name: signalName, value: filterKeys }];
            }

            default: {
                return defaultReturn;
            }
        }
    },
});

function omitFiltersInCurrentDrill(
    filters: QueryFilterDataItem[],
    settings: Partial<SettingValueMap>,
    definition: HighlightOnFilterInCurrentDrillSettingDefinition,
) {
    if (!settings.DRILL || !settings.DRILL.length || !definition.value) return [];

    const currentDrillKeys = settings.DRILL.map(({ key }) => key);

    return filters.filter(
        filter =>
            (filter.type === 'HIERARCHY' || filter.type === 'DIMENSION') &&
            filter.object &&
            currentDrillKeys.includes(filter.object),
    );
}
